import { configureAttribution, consumeAttributionHandoffParam } from "@next-platform/client-common-core";
import { ATTRIBUTION_SIGNING_SECRET } from "@next-platform/client-common-core/infra/constants";
import appLogger from "@next-platform/client-common-core/logger/appLogger";
import { ATTRIBUTION_HANDOFF_PARAM } from "@next-platform/packages-ts-types";
import { useEffect } from "react";

import { captureAttributionFromCurrentWindow } from "@/features/attribution/captureFromCurrentWindow";
import { createWebCryptoHmac } from "@/features/attribution/createWebCryptoHmac";

const attributionLogger = appLogger.withTag("AttributionTracking");

function stripHandoffParamFromUrl(): void {
  const url = new URL(window.location.href);
  url.searchParams.delete(ATTRIBUTION_HANDOFF_PARAM);
  window.history.replaceState(window.history.state, "", url.toString());
}

// Called from a `finally` block where rethrowing would mask the original
// error. Swallowing and logging here keeps the boot sequence robust.
function safelyStripHandoffParamFromUrl(): void {
  try {
    stripHandoffParamFromUrl();
  } catch (error) {
    attributionLogger.warn("stripHandoffParamFromUrl failed", { error });
  }
}

async function consumeAndLogHandoff(handoffParam: string): Promise<void> {
  const consumed = await consumeAttributionHandoffParam(handoffParam);
  if (consumed === undefined) {
    attributionLogger.warn("Rejected inbound handoff (malformed / unsupported version)");
    return;
  }
  if (!consumed.isSignatureVerified) {
    attributionLogger.log("Accepted unsigned handoff (non-blocking policy)");
  }
}

/**
 * Invoked whenever `?ad_h` is present on the URL, including the empty
 * `?ad_h=` case. An empty value carries no envelope to consume, but the
 * param must still be stripped — otherwise it leaks into analytics,
 * browser history, and shared URLs, which is the exact scenario the
 * handoff scheme is designed to prevent. The `finally` clause guarantees
 * the strip runs on every path (throw, empty value, or successful
 * consume).
 */
async function consumeAndStripHandoffParam(handoffParam: string): Promise<void> {
  try {
    if (handoffParam !== "") {
      await consumeAndLogHandoff(handoffParam);
    }
  } catch (error) {
    attributionLogger.warn("Handoff consumption threw; stripping param anyway", { error });
  } finally {
    safelyStripHandoffParamFromUrl();
  }
}

async function configureAttributionHostFromEnv(): Promise<void> {
  const rawSecret = ATTRIBUTION_SIGNING_SECRET;
  if (rawSecret === undefined || rawSecret === "") {
    return;
  }
  const secretBytes = new TextEncoder().encode(rawSecret);
  const hmac = await createWebCryptoHmac(secretBytes);
  if (hmac === undefined) {
    attributionLogger.warn("Web Crypto unavailable; envelopes will be unsigned");
    return;
  }
  configureAttribution({ signer: hmac.signer, verifier: hmac.verifier });
}

/**
 * Tracks whether boot has already run for the current page load. Guards
 * against any second invocation within the same load (StrictMode toggle,
 * component remount, HMR, Suspense boundary re-render) re-running the
 * whole boot sequence. Not strictly required today since this shim only
 * strips `ad_h` (click ids stay on the URL, so re-capture would be
 * idempotent at the envelope level), but kept as defense-in-depth so a
 * future click-id strip cannot silently overwrite lastTouch with a
 * UTM-only post-strip version.
 */
let hasBootCompleted = false;

/**
 * Each step below is guarded independently so that a failure in one stage
 * never cancels the others. Specifically:
 *
 * - Host configuration failure must not prevent handoff consumption or
 *   capture (the app still works unsigned).
 * - Handoff consumption failure must not prevent `stripHandoffParamFromUrl`
 *   from running — otherwise `?ad_h=` leaks into analytics, browser history
 *   and shared links, the exact risk the handoff scheme aims to prevent.
 * - Capture failure must not surface as an unhandled rejection; attribution
 *   is observational and must never break the SPA boot.
 *
 * Idempotent: repeat calls within the same page load are no-ops.
 */
async function initializeAttribution(): Promise<void> {
  if (hasBootCompleted) {
    return;
  }
  hasBootCompleted = true;

  try {
    await configureAttributionHostFromEnv();
  } catch (error) {
    attributionLogger.warn("Host configuration failed; continuing unsigned", { error });
  }

  const handoffParam = new URLSearchParams(window.location.search).get(ATTRIBUTION_HANDOFF_PARAM);
  if (handoffParam !== null) {
    await consumeAndStripHandoffParam(handoffParam);
  }

  try {
    await captureAttributionFromCurrentWindow();
  } catch (error) {
    attributionLogger.warn("Capture failed; envelope may be stale", { error });
  }
}

/**
 * Boot-time attribution wiring. Runs once at app startup:
 *
 * 1. Configures the `@next-platform/client-common-core` attribution
 *    host with an HMAC signer/verifier derived from the publishable
 *    `VITE_ATTRIBUTION_SIGNING_SECRET`. When the env var is absent
 *    (local dev, first-cut production), the host stays unsigned — the
 *    handoff still works, signatures are just non-blocking per plan
 *    §8.2.
 * 2. Consumes an inbound `?ad_h=<envelope>` handoff from a cross-domain
 *    landing redirect (e.g. `landing.spicychat.ai` → `spicychat.ai`),
 *    merges it into the local envelope, and strips the param from the
 *    URL so it doesn't pollute analytics or get shared by users.
 * 3. Captures the current window (`location.href`, `document.referrer`,
 *    `document.cookie`) to record any fresh ad click. Click ids and
 *    platform Pixel cookies (`_fbp`, `_fbc`, `_gcl_au`, ...) land on
 *    the envelope for later cross-domain dispatch and server-side CAPI.
 *
 * Renders nothing; pairs with boot-time `persistAffiliateFromUrl` (legacy
 * `aff_id` path) for one more release while the envelope replaces it.
 */
const AttributionTracking = (): null => {
  useEffect(() => {
    void initializeAttribution();
  }, []);

  return null;
};

export default AttributionTracking;
